import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useCallback, useRef, useState } from 'react';
// next
import { useRouter } from 'next/router';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Stack, IconButton, InputAdornment, Alert, Link } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import DIOText from '../../../components/DIOText';
import Turnstile from '../../../components/Turnstile';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';
// config
import { EVENT_ID, INPUT_ID } from '../../../config';

// ----------------------------------------------------------------------

// 백엔드 정책과 맞춘 값. 최소 길이는 Django MinimumLengthValidator 기본값,
// 최대 길이는 가입 serializer 의 max_length 다.
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 50;

// ----------------------------------------------------------------------

export default function RegisterForm() {
  const { register } = useAuth();
  const router = useRouter();
  const { from='' } = router.query;

  const { enqueueSnackbar } = useSnackbar();

  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);

  const turnstileRef = useRef(null);

  const [turnstileToken, setTurnstileToken] = useState('');

  // 위젯이 죽었을 때(스크립트 차단, Cloudflare 장애) 가입 자체가 막히지 않도록 제출을 다시 허용한다.
  // 백엔드도 같은 이유로 fail-open 이다.
  const [turnstileFailed, setTurnstileFailed] = useState(false);

  // 토큰은 1회용이라, 발급받은 토큰을 한 번 쓴 뒤에는 위젯을 새로 돌려야 한다
  const resetTurnstile = useCallback(() => {
    setTurnstileToken('');
    turnstileRef.current?.reset();
  }, []);

  const handleTurnstileVerify = useCallback((token) => {
    setTurnstileToken(token);
    setTurnstileFailed(false);
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken('');
    setTurnstileFailed(true);
  }, []);

  const RegisterSchema = Yup.object().shape({
    email: Yup.string().trim().email('이메일을 입력하세요').required('이메일을 입력하세요'),
    // 백엔드 AUTH_PASSWORD_VALIDATORS 와 같은 규칙.
    // 흔한 비밀번호 / 이메일과 유사 여부는 서버만 판정할 수 있어 여기서는 검사하지 않는다.
    password: Yup.string()
      .trim()
      .required('비밀번호를 입력하세요')
      .min(PASSWORD_MIN_LENGTH, `비밀번호는 ${PASSWORD_MIN_LENGTH}자 이상이어야 합니다`)
      .max(PASSWORD_MAX_LENGTH, `비밀번호는 ${PASSWORD_MAX_LENGTH}자 이하로 입력하세요`)
      .matches(/\D/, '숫자로만 만들 수 없습니다. 영문자를 함께 사용해주세요'),
  });

  const defaultValues = {
    email: '',
    password: '',
    allowMarketing: false,
  };

  const methods = useForm({
    resolver: yupResolver(RegisterSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    try {
      await register(data.email, data.password, data.allowMarketing, from, turnstileToken);
      enqueueSnackbar('인증 메일을 발송하였습니다', { variant: 'info' })
    } catch (error) {
      console.error(error);
      resetTurnstile();
      // 봇 검증 실패와 비밀번호 정책 위반이 모두 이 형태로 온다.
      // 서버가 사람이 읽을 문구를 주므로 그대로 노출하고, 입력값은 지우지 않는다
      const serverMessage = error.response && error.response.status === 400 && error.response.data?.message;
      if (serverMessage) {
        setError('afterSubmit', { type: "manual", message: serverMessage });
      } else if (error.response && error.response.status === 409) {
        setError('afterSubmit', { type: "manual", message: "이미 가입된 이메일입니다" });
      } else {
        reset();
        if (isMountedRef.current) {
          setError('afterSubmit', { ...error, message: error.message });
        }
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField name="email" label="이메일" id={INPUT_ID.registerEmail} />

        <RHFTextField
          name="password"
          label="비밀번호"
          helperText={`${PASSWORD_MIN_LENGTH}자 이상, 숫자로만 구성할 수 없습니다`}
          id={INPUT_ID.registerPassword}
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" alignItems={"center"} justifyContent={"space-between"}>
          <RHFCheckbox
            name="allowMarketing"
            label="할인 쿠폰, 채용 정보 등 마케팅 정보수신"
          />
          <Link href={"https://spacey-dio.notion.site/DIO-122b0a107b154feb886dcec094a70940"} target="_blank" rel="noopener" sx={{ height: '20px' }}>
            <Iconify icon={'ic:baseline-chevron-right'} sx={{ width: '20px', height: '20px' }} />
          </Link>
        </Stack>

        <Turnstile
          ref={turnstileRef}
          onVerify={handleTurnstileVerify}
          onExpire={resetTurnstile}
          onError={handleTurnstileError}
        />

        {turnstileFailed && (
          <Alert severity="warning">
            보안 확인을 불러오지 못했습니다. 새로고침 후에도 같으면 다른 브라우저나 네트워크에서 시도해 주세요.
          </Alert>
        )}

        <LoadingButton
          id={EVENT_ID.bizRegister}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          disabled={!turnstileToken && !turnstileFailed}
        >
          <DIOText sx={{ mr: 0.5 }} /> 가입하기
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
}
